import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import { Icon } from './Icon';

export function Card({
  title,
  aside,
  flush = false,
  className = '',
  children,
  ...rest
}: HTMLAttributes<HTMLElement> & {
  title?: ReactNode;
  aside?: ReactNode;
  flush?: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <section className={`card ${flush ? 'card-flush' : ''} ${className}`} {...rest}>
      {title ? (
        <header
          className={`card-head ${flush ? 'row' : ''}`}
          style={flush ? { marginBottom: 0 } : undefined}
        >
          <h2 className="t-heading">{title}</h2>
          {aside}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function Rows({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}): ReactElement {
  return <div className={`rows ${className}`}>{children}</div>;
}

/** Nothing here — and why. Empty because nothing happened, not because something is missing. */
export function Empty({
  tone = 'none',
  lead,
  children,
  action,
}: {
  tone?: 'ok' | 'none' | 'bad';
  lead: string;
  children?: ReactNode;
  action?: ReactNode;
}): ReactElement {
  const icon = tone === 'ok' ? 'check' : tone === 'bad' ? 'alert' : 'clock';
  return (
    <div className="empty" role={tone === 'bad' ? 'alert' : undefined}>
      <span className={`empty-glyph ${tone === 'ok' ? 'ok' : tone === 'bad' ? 'bad' : ''}`}>
        <Icon name={icon} size={16} />
      </span>
      <p className="lead">{lead}</p>
      {children ? <p className="t-secondary">{children}</p> : null}
      {action ? (
        <div className="cluster" style={{ marginTop: 6 }}>
          {action}
        </div>
      ) : null}
    </div>
  );
}

/** Skeletons match the real row's geometry, so nothing jumps when the data lands. */
export function SkeletonRows({
  rows = 3,
  dense = false,
}: {
  rows?: number;
  dense?: boolean;
}): ReactElement {
  return (
    <div className="rows" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={`row ${dense ? 'dense' : ''}`}>
          <span className="skeleton" style={{ width: 18, height: 18, borderRadius: 9 }} />
          <div className="row-main">
            <span className="skeleton" style={{ width: `${52 + ((i * 17) % 30)}%` }} />
            <span className="skeleton" style={{ width: `${28 + ((i * 11) % 20)}%`, height: 11 }} />
          </div>
          <span className="skeleton" style={{ width: 56, height: 20, borderRadius: 999 }} />
        </div>
      ))}
    </div>
  );
}

/** This page could not load — nothing was lost, it is all on the server. */
export function Broken({ what, retry }: { what: string; retry: () => void }): ReactElement {
  return (
    <Empty
      tone="bad"
      lead={`${what} could not load.`}
      action={
        <button type="button" className="btn" onClick={retry}>
          Try again
        </button>
      }
    >
      Your connection dropped while we were fetching it. Nothing was lost — it is all on the server.
    </Empty>
  );
}
